import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Check, ArrowLeft } from "lucide-react";
import { toast, Toaster } from "sonner";

import { DEPOSIT } from "@/constants/testIds";
import { BRAND } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const initial = {
  name: "",
  email: "",
  phone: "",
  proposal_ref: "",
  amount_inr: "",
  note: "",
};

export default function Deposit() {
  const [form, setForm] = useState(initial);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [errors, setErrors] = useState({});

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/razorpay/config`);
        setConfig(data);
      } catch (e) {
        setConfig({ configured: false });
      }
    })();
    (async () => {
      try {
        const { data } = await axios.get(`${API}/payments/new-ref`);
        if (data?.proposal_ref) set("proposal_ref", data.proposal_ref);
      } catch (e) {
        /* non-blocking — ref stays blank if this fails */
      }
    })();
  }, []);

  const setPreset = (pct) => {
    const total = Number(form.amount_inr || 0);
    // Presets multiply what's typed as the deposit percentage of a proposal total.
    // If the user typed the proposal total, this sets amount to pct% of it.
    if (total <= 0) {
      toast.info("Enter your proposal total first, then pick a percentage.");
      return;
    }
    set("amount_inr", String(Math.round((total * pct) / 100)));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2)
      e.name = "Please share your name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Enter a valid email.";
    if (!form.phone.trim() || form.phone.trim().length < 6)
      e.phone = "Enter a valid phone number.";
    if (!form.proposal_ref.trim())
      e.proposal_ref = "The reference on your proposal.";
    if (!form.amount_inr || Number(form.amount_inr) <= 0)
      e.amount_inr = "Enter the amount to pay.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("A few fields need attention.");
      return;
    }
    if (!config?.configured) {
      toast.error(
        "Payments are not configured yet. Please contact us directly."
      );
      return;
    }
    setLoading(true);

    const ok = await loadRazorpay();
    if (!ok) {
      setLoading(false);
      toast.error("Could not load payment gateway. Check your connection.");
      return;
    }

    try {
      const { data: order } = await axios.post(
        `${API}/payments/create-order`,
        {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          proposal_ref: form.proposal_ref.trim(),
          amount_inr: Number(form.amount_inr),
          note: form.note.trim(),
        }
      );

      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount_paise,
        currency: order.currency,
        name: "Indian Alchemy",
        description: `Booking deposit · ${form.proposal_ref}`,
        order_id: order.order_id,
        prefill: {
          name: order.name,
          email: order.email,
          contact: order.phone,
        },
        theme: { color: "#1A1A1A" },
        handler: async (resp) => {
          try {
            await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setSuccess({
              order_id: resp.razorpay_order_id,
              payment_id: resp.razorpay_payment_id,
              amount: form.amount_inr,
              name: form.name,
            });
            toast.success("Deposit received — thank you.");
          } catch (e) {
            toast.error(
              e?.response?.data?.detail || "Payment verification failed."
            );
          }
        },
        modal: {
          ondismiss: () => {
            toast("Payment closed. You can try again anytime.");
          },
        },
      });

      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed. Please try again.");
      });

      rzp.open();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Could not initiate payment. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b border-[#F7F5F0]/25 focus:border-[#C6A87C] outline-none py-3 text-[#F7F5F0] placeholder:text-[#F7F5F0]/40 font-sans text-base focus:ring-0";

  if (success) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] text-[#F7F5F0]">
        <Toaster position="bottom-right" />
        <div className="max-w-3xl mx-auto px-6 md:px-10 pt-32 pb-24" data-testid={DEPOSIT.successCard}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center justify-center h-16 w-16 rounded-full border border-[#C6A87C] text-[#C6A87C]"
          >
            <Check size={26} />
          </motion.div>
          <h1 className="mt-10 font-serif font-light text-4xl md:text-6xl leading-[1.05]">
            Deposit received.<br />
            <em className="italic text-[#C6A87C]">Your date is held.</em>
          </h1>
          <p className="mt-6 text-[#F7F5F0]/70 leading-relaxed max-w-xl">
            Thank you, {success.name}. A confirmation has been sent to our team
            and our team will be in touch within one working day to walk through
            next steps, tastings, and menu finalisation.
          </p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm border-t border-white/10 pt-8">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#C6A87C]">Amount</div>
              <div className="mt-1 font-serif text-2xl">₹ {Number(success.amount).toLocaleString("en-IN")}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#C6A87C]">Order</div>
              <div className="mt-1 font-mono text-xs break-all">{success.order_id}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#C6A87C]">Payment</div>
              <div className="mt-1 font-mono text-xs break-all">{success.payment_id}</div>
            </div>
          </div>
          <Link
            to="/"
            data-testid={DEPOSIT.backHome}
            className="mt-14 inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[#C6A87C] border-b border-[#C6A87C]/60 pb-1 hover:text-[#F7F5F0]"
          >
            <ArrowLeft size={14} /> Back to Indian Alchemy
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1A1A1A] text-[#F7F5F0] relative overflow-hidden">
      <Toaster position="bottom-right" />

      {/* Left rail image / composition */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#F7F5F0]/60 hover:text-[#C6A87C]"
        >
          <ArrowLeft size={14} /> Indian Alchemy
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
        {/* Copy column */}
        <div className="lg:col-span-5">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#C6A87C]">
            Client Payments — Booking Deposit
          </div>
          <h1 className="mt-6 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
            Lock the date.<br />
            <em className="italic text-[#C6A87C]">Everything else follows.</em>
          </h1>
          <p className="mt-6 text-[#F7F5F0]/70 leading-relaxed max-w-md">
            Once we&rsquo;ve sent you a proposal, a {config?.deposit_percent ?? 25}%
            deposit holds your date on our calendar and lets us start sourcing,
            staffing, and locking suppliers.
          </p>

          <div className="mt-14 space-y-6">
            <div className="flex items-start gap-4">
              <ShieldCheck className="text-[#C6A87C]" size={18} />
              <div>
                <div className="font-serif text-lg">Secure payments by Razorpay</div>
                <div className="text-sm text-[#F7F5F0]/60 mt-1">
                  Card, UPI, netbanking and wallets. PCI-DSS compliant.
                </div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <ShieldCheck className="text-[#C6A87C]" size={18} />
              <div>
                <div className="font-serif text-lg">Refundable per proposal</div>
                <div className="text-sm text-[#F7F5F0]/60 mt-1">
                  Full refund up to 30 days before event date. Details on your proposal.
                </div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <ShieldCheck className="text-[#C6A87C]" size={18} />
              <div>
                <div className="font-serif text-lg">Instant confirmation</div>
                <div className="text-sm text-[#F7F5F0]/60 mt-1">
                  You&rsquo;ll get a receipt on-screen and by email.
                </div>
              </div>
            </div>
          </div>

          <div className="mt-14 pt-8 border-t border-white/10 text-sm text-[#F7F5F0]/60">
            Questions? Reach us directly at{" "}
            <a
              href={`mailto:${BRAND.email}`}
              className="text-[#C6A87C] border-b border-[#C6A87C]/50 hover:text-[#F7F5F0]"
            >
              {BRAND.email}
            </a>
            .
          </div>
        </div>

        {/* Form column */}
        <form
          onSubmit={submit}
          data-testid={DEPOSIT.form}
          className="lg:col-span-7 bg-[#111] p-8 md:p-12 border border-white/10"
          noValidate
        >
          {!config?.configured && (
            <div className="mb-8 p-4 border border-[#C6A87C]/40 bg-[#C6A87C]/5 text-sm text-[#C6A87C]">
              Payments are not fully configured yet. Our team is aware — please
              email us directly and we&rsquo;ll share an alternate secure link.
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
            <Field label="Full Name" error={errors.name}>
              <input
                data-testid={DEPOSIT.name}
                className={inputCls}
                placeholder="Your name"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            </Field>
            <Field label="Email" error={errors.email}>
              <input
                data-testid={DEPOSIT.email}
                type="email"
                className={inputCls}
                placeholder="you@company.com"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
              />
            </Field>
            <Field label="Phone" error={errors.phone}>
              <input
                data-testid={DEPOSIT.phone}
                type="tel"
                className={inputCls}
                placeholder="+91 …"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
              />
            </Field>
            <Field label="Proposal Reference" error={errors.proposal_ref}>
              <input
                data-testid={DEPOSIT.proposalRef}
                className={`${inputCls} !text-[#C6A87C] cursor-not-allowed`}
                readOnly
                value={form.proposal_ref || "Generating…"}
              />
              <span className="mt-1 block text-[10px] text-[#F7F5F0]/40 normal-case tracking-normal">
                Auto-generated — quote this reference in any correspondence.
              </span>
            </Field>

            <div className="sm:col-span-2">
              <Field label="Amount to Pay (INR)" error={errors.amount_inr}>
                <div className="flex items-center gap-3">
                  <span className="font-serif text-3xl text-[#C6A87C]">₹</span>
                  <input
                    data-testid={DEPOSIT.amount}
                    type="number"
                    min={1}
                    step="1"
                    className={`${inputCls} !text-3xl font-serif`}
                    placeholder="0"
                    value={form.amount_inr}
                    onChange={(e) => set("amount_inr", e.target.value)}
                  />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <PresetBtn testId={DEPOSIT.amount25} onClick={() => setPreset(25)} label={`${config?.deposit_percent ?? 25}% deposit`} />
                  <PresetBtn testId={DEPOSIT.amount50} onClick={() => setPreset(50)} label="50% milestone" />
                  <PresetBtn testId={DEPOSIT.amount100} onClick={() => setPreset(100)} label="Full amount" />
                </div>
                <div className="mt-2 text-xs text-[#F7F5F0]/50">
                  Tip: enter your proposal total, then tap a preset to auto-calculate.
                </div>
              </Field>
            </div>

            <div className="sm:col-span-2">
              <Field label="Note (optional)">
                <textarea
                  data-testid={DEPOSIT.note}
                  rows={3}
                  className={`${inputCls} resize-none`}
                  placeholder="Anything we should note against this payment."
                  value={form.note}
                  onChange={(e) => set("note", e.target.value)}
                />
              </Field>
            </div>

            <div className="sm:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-6 border-t border-white/10">
              <p className="text-xs text-[#F7F5F0]/50 max-w-md leading-relaxed">
                By continuing you agree to our booking terms as stated in your
                proposal. Payments are processed by Razorpay.
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid={DEPOSIT.submit}
                className="group inline-flex items-center gap-3 h-12 pl-6 pr-5 rounded-full bg-[#C6A87C] text-[#1A1A1A] text-xs uppercase tracking-[0.28em] hover:bg-[#F7F5F0] transition-colors disabled:opacity-60 whitespace-nowrap"
              >
                {loading ? "Preparing…" : "Pay Deposit"}
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#1A1A1A] text-[#C6A87C] group-hover:rotate-45 transition-transform duration-500">
                  <ArrowRight size={16} />
                </span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.3em] text-[#F7F5F0]/60 mb-1">
        {label}
      </span>
      {children}
      {error && (
        <span className="mt-1 block text-xs text-[#C6A87C]">{error}</span>
      )}
    </label>
  );
}

function PresetBtn({ testId, onClick, label }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="text-[10px] uppercase tracking-[0.25em] px-3 h-8 rounded-full border border-white/20 text-[#F7F5F0]/80 hover:border-[#C6A87C] hover:text-[#C6A87C] transition-colors"
    >
      {label}
    </button>
  );
}
